// Views API — extracted from api.js (Tier 2 #9a round 2).
// Record / detail / activity views, chart templates, and chart screens. All
// appId-based (UUID-keyed under /apps/:appId/), plus legacy-signature shims
// that resolve a numeric template_id to its uuid via the per-app list.
//
// The 4 versionId-based fetchers (fetchRecordViews, fetchDetailViews,
// fetchActivityViewsByWorkflow, fetchChartTemplates) live in workflowApi.js
// with the rest of the version-scoped cluster and take { appId } explicitly
// (B6/S1 completed the Tier 2 #9b migration).

import api from './api';
import { absorbAllDataFields } from './fieldPayload';

// Record-view fetchers (fetchRecordView, fetchRecordViewByUUID,
// fetchRecordViewsByApp, fetchRecordViewFieldImpactByUUID, CRUD) live in
// api.js and are re-exported via the barrel — they used to be duplicated here,
// but api.js's local exports shadowed these, so this copy was dead code.

// ─── Detail Views (UUID-keyed under /apps/:appId/) ─────────────────────────
export const fetchDetailViewsByApp = async (appId, { signal } = {}) => {
  const response = await api.get(`/dashboard/apps/${appId}/detail-views`, { signal });
  return response.data;
};

// includeFields: see fetchRecordViewByUUID in api.js.
export const fetchDetailViewByUUID = async (appId, dvUid, { includeFields = false } = {}) => {
  const response = await api.get(`/apps/${appId}/detail-views/${dvUid}`, {
    ...(includeFields ? { params: { include: 'fields' } } : {}),
  });
  return absorbAllDataFields(response.data);
};

export const createDetailView = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/detail-views`, data);
  return response.data;
};

export const updateDetailViewByUUID = async (appId, dvUid, data, options = {}) => {
  const body = {
    ...data,
    ...(options.allowedDeleteFieldKeys?.length ? { allowed_delete_field_keys: options.allowedDeleteFieldKeys } : {}),
    ...(options.ackCrossVersionDrops ? { acknowledge_cross_version_field_drops: true } : {}),
  };
  const response = await api.put(`/apps/${appId}/detail-views/${dvUid}`, body);
  return response.data;
};

export const deleteDetailViewByUUID = async (appId, dvUid) => {
  const response = await api.delete(`/apps/${appId}/detail-views/${dvUid}`);
  return response.data;
};

// See fetchRecordViewUsage — same contract for DV deletes.
export const fetchDetailViewUsage = async (appId, dvUid, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/detail-views/${dvUid}/usage`, { signal });
  return response.data;
};

export const fetchDetailViewFieldImpactByUUID = async (appId, dvUid, fieldId) => {
  const response = await api.get(`/apps/${appId}/detail-views/${dvUid}/fields/${fieldId}/impact`);
  return response.data;
};

// Legacy-signature shim — same pattern as fetchRecordView from Z.7/1. View
// rows still carry dv_template_id integer FK in layout JSONB; the shim
// resolves dv_uid via the per-app list and forwards to the uuid endpoint.
export const fetchDetailView = async (appId, dvId, { signal } = {}) => {
  // Fetch one detail view by its integer PK. The by-id endpoint resolves the
  // owning app internally (authz-scoped), so no per-app list is needed.
  const response = await api.get(`/detail-views/${dvId}`, { signal });
  return response.data;
};

// ─── Activity Views (UUID-keyed under /apps/:appId/) ───────────────────────
export const fetchActivityViewsByApp = async (appId, { signal } = {}) => {
  const response = await api.get(`/dashboard/apps/${appId}/activity-views`, { signal });
  return response.data;
};

// includeFields: see fetchRecordViewByUUID in api.js.
export const fetchActivityViewByUUID = async (appId, avUid, { includeFields = false } = {}) => {
  const response = await api.get(`/apps/${appId}/activity-views/${avUid}`, {
    ...(includeFields ? { params: { include: 'fields' } } : {}),
  });
  return absorbAllDataFields(response.data);
};

export const createActivityView = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/activity-views`, data);
  return response.data;
};

export const updateActivityViewByUUID = async (appId, avUid, data, options = {}) => {
  const body = {
    ...data,
    ...(options.ackCrossVersionDrops ? { acknowledge_cross_version_field_drops: true } : {}),
  };
  const response = await api.put(`/apps/${appId}/activity-views/${avUid}`, body);
  return response.data;
};

export const deleteActivityViewByUUID = async (appId, avUid) => {
  const response = await api.delete(`/apps/${appId}/activity-views/${avUid}`);
  return response.data;
};

// See fetchRecordViewUsage — same { dependents: [{id, uuid, name, device}] } contract.
export const fetchActivityViewUsage = async (appId, avUid, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/activity-views/${avUid}/usage`, { signal });
  return response.data;
};

// Legacy-signature shim — view rows carry av_template_id integer FK in
// AV-screen rows; the shim resolves av_uid via the per-app list.
export const fetchActivityView = async (appId, avId, { signal } = {}) => {
  // Fetch one activity view by its integer PK. The by-id endpoint resolves the
  // owning app internally (authz-scoped), so no per-app list is needed.
  const response = await api.get(`/activity-views/${avId}`, { signal });
  return response.data;
};

// ─── Chart Templates (UUID-keyed under /apps/:appId/) ──────────────────────
export const fetchChartTemplatesByApp = async (appId, { signal } = {}) => {
  const response = await api.get(`/dashboard/apps/${appId}/chart-templates`, { signal });
  return response.data;
};

// includeFields: see fetchRecordViewByUUID in api.js.
export const fetchChartTemplateByUUID = async (appId, ctUid, { includeFields = false } = {}) => {
  const response = await api.get(`/apps/${appId}/chart-templates/${ctUid}`, {
    ...(includeFields ? { params: { include: 'fields' } } : {}),
  });
  return absorbAllDataFields(response.data);
};

export const createChartTemplate = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/chart-templates`, data);
  return response.data;
};

export const updateChartTemplateByUUID = async (appId, ctUid, data, options = {}) => {
  const body = {
    ...data,
    ...(options.allowedDeleteFieldKeys?.length ? { allowed_delete_field_keys: options.allowedDeleteFieldKeys } : {}),
    ...(options.ackCrossVersionDrops ? { acknowledge_cross_version_field_drops: true } : {}),
  };
  const response = await api.put(`/apps/${appId}/chart-templates/${ctUid}`, body);
  return response.data;
};

export const deleteChartTemplateByUUID = async (appId, ctUid) => {
  const response = await api.delete(`/apps/${appId}/chart-templates/${ctUid}`);
  return response.data;
};

// See fetchRecordViewUsage — same { dependents: [{id, uuid, name, device}] } contract.
export const fetchChartTemplateUsage = async (appId, ctUid, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/chart-templates/${ctUid}/usage`, { signal });
  return response.data;
};

export const fetchChartTemplateFieldImpactByUUID = async (appId, ctUid, fieldId) => {
  const response = await api.get(`/apps/${appId}/chart-templates/${ctUid}/fields/${fieldId}/impact`);
  return response.data;
};

// Screen embed "where-used": which screens EMBED this sub-screen in their builder
// layout (distinct from fetch*ViewUsage above, which is template-id binding).
// Same { dependents: [{ uuid, name, kind, device }] } contract + abort support.
export const fetchRvScreenUsage = async (appId, rvScreenUuid, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/rv-screens/${rvScreenUuid}/usage`, { signal });
  return response.data;
};

export const fetchDvScreenUsage = async (appId, dvScreenUuid, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/dv-screens/${dvScreenUuid}/usage`, { signal });
  return response.data;
};

export const fetchAvScreenUsage = async (appId, avScreenUuid, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/av-screens/${avScreenUuid}/usage`, { signal });
  return response.data;
};

export const fetchChartScreenUsage = async (appId, chartScreenUuid, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/chart-screens/${chartScreenUuid}/usage`, { signal });
  return response.data;
};

// Legacy-signature shims (chart-screens / tile / chart settings read
// ct_template_id integer FK from layout JSONB).
export const fetchChartTemplate = async (appId, ctId, { signal } = {}) => {
  // Fetch one chart template by its integer PK. The by-id endpoint resolves the
  // owning app internally (authz-scoped), so no per-app list is needed.
  const response = await api.get(`/chart-templates/${ctId}`, { signal });
  return response.data;
};

// ─── Chart Screens ─────────────────────────────────────────────────────────
export const fetchChartScreens = async (appId, { signal } = {}) => {
  const response = await api.get(`/apps/${appId}/chart-screens`, { signal });
  return response.data;
};

export const createChartScreen = async (appId, data) => {
  const response = await api.post(`/apps/${appId}/chart-screens`, data);
  return response.data;
};
